import React, { useEffect, useState } from "react";

import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import ClientRiskTable, {
  ClientRiskData,
} from "~/pages/risk/components/ClientRiskTable";
import { Seller, fetchSellerDetails } from "~/services/sellerService";
import { SalesRequest } from "~/types/client";
import { formatDate } from "~/utils/helpers";

interface SellerDetailsProps {
  seller: Seller;
}

const SellerDetails: React.FC<SellerDetailsProps> = ({ seller }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [clients, setClients] = useState<ClientRiskData[]>([]);
  const [salesRequests, setSalesRequests] = useState<SalesRequest[]>([]);

  useEffect(() => {
    const loadSellerDetails = async () => {
      setIsLoading(true);
      try {
        const { clients, salesRequests } = await fetchSellerDetails(seller.id);

        // Convert clients to ClientRiskData format
        const formattedClients: ClientRiskData[] = clients.map((client) => ({
          id: client.id,
          name: client.name,
          company: client.name, // Using name as company since the actual field might be missing
          taxId: client.tax_id || "",
          email: client.email || "",
          insuredAmount: client.insured_amount || 0,
          trailingPaymentDays: client.trailing_payment_days || 0,
          createdAt: client.created_at,
        }));

        setClients(formattedClients);
        setSalesRequests(salesRequests);
      } catch (error) {
        console.error("Error loading seller details:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadSellerDetails();
  }, [seller.id]);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Cargando detalles...</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[200px] w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="break-words">
          {seller.name} {seller.lastname}{" "}
          <span className="text-sm font-normal text-muted-foreground">
            ({seller.email})
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="clients">
          <TabsList className="mb-4 w-full justify-start overflow-x-auto">
            <TabsTrigger value="clients">
              Clientes ({clients.length})
            </TabsTrigger>
            <TabsTrigger value="sales">
              Notas de Venta ({salesRequests.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="clients">
            {clients.length > 0 ? (
              <ClientRiskTable
                clients={clients}
                showActions={false}
                isLoading={false}
              />
            ) : (
              <div className="py-6 text-center text-muted-foreground">
                No hay clientes asignados a este vendedor
              </div>
            )}
          </TabsContent>

          <TabsContent value="sales">
            {salesRequests.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="whitespace-nowrap">Fecha</TableHead>
                      <TableHead className="whitespace-nowrap">
                        Cliente
                      </TableHead>
                      <TableHead className="whitespace-nowrap">Monto</TableHead>
                      <TableHead className="whitespace-nowrap">
                        Estado Original
                      </TableHead>
                      <TableHead className="whitespace-nowrap">
                        Pagado
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {salesRequests.map((request, index) => (
                      <TableRow
                        key={request.id}
                        style={{
                          animationDelay: `${Math.min(index * 40, 400)}ms`,
                        }}
                        className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                      >
                        <TableCell>{formatDate(request.createdAt)}</TableCell>
                        <TableCell>{request.clientId}</TableCell>
                        <TableCell>
                          ${request.totalAmount?.toLocaleString("es-CL")}
                        </TableCell>
                        <TableCell>
                          {request.status === "pending" &&
                            "Pendiente Aprobación"}
                          {request.status === "accepted" && "Aprobada"}
                          {request.status === "rejected" && "Rechazada"}
                          {request.status === "in_transit" && "En Camino"}
                          {request.status === "delivered" && "Entregado"}
                        </TableCell>
                        <TableCell>
                          <span
                            className={`rounded-full px-2 py-1 text-xs font-medium ${
                              request.status === "accepted"
                                ? "bg-green-100 text-green-800"
                                : request.status === "rejected"
                                  ? "bg-red-100 text-red-800"
                                  : request.status === "pending"
                                    ? "bg-yellow-100 text-yellow-800"
                                    : request.status === "in_transit"
                                      ? "bg-blue-100 text-blue-800"
                                      : request.status === "delivered"
                                        ? "bg-purple-100 text-purple-800"
                                        : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {request.status}
                          </span>
                        </TableCell>
                        <TableCell>{request.paid ? "Sí" : "No"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="py-6 text-center text-muted-foreground">
                Este vendedor no tiene notas de venta
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default SellerDetails;
